<script setup lang="ts">
import type { AxiosError } from 'axios';
import { useMutation, useQueryClient, useQuery } from '@tanstack/vue-query';
import { useForm } from 'vee-validate';
import { object, number, boolean } from 'yup';
import { computed, ref, watch } from 'vue';
import { formatCurrency } from '@/utils/currency';
import { caregiversApi } from '@/api/caregivers';
import { baseServicePricesApi } from '@/api/base-service-prices';
import { usePermissions } from '@/composables/usePermissions';
import PriceInput from '@/components/price-input.vue';
import InfoField from '@/components/info-field.vue';
import SectionHeader from '@/components/section-header.vue';

const props = withDefaults(defineProps<{
  caregiverId: number;
  inline?: boolean;
}>(), {
  inline: false,
});

const emit = defineEmits(['close']);

const { hasPermission } = usePermissions();
const queryClient = useQueryClient();

const showForm = ref(false);
const showSuccessAlert = ref(false);

const { data: caregiver, isLoading: isCaregiverLoading } = useQuery({
  queryKey: ['caregiver', props.caregiverId],
  queryFn: () => caregiversApi.show(props.caregiverId),
});

const career = computed(() => caregiver.value?.career);

const { data: baseServicePrice, isLoading: isBaseServicePriceLoading } = useQuery({
  queryKey: ['baseServicePrice', career],
  queryFn: () => baseServicePricesApi.show(career.value || '')
    .catch(() => ({
      weekCost: 0,
      weekendCost: 0,
      weekPrice: 0,
      weekendPrice: 0,
      withTaxBill: false,
    })),
  enabled: computed(() => !!career.value),
});

const hasExistingCosts = computed(() =>
  caregiver.value?.defaultWeekCost !== null && caregiver.value?.defaultWeekendCost !== null,
);

function formatCostValue(cost: number | undefined): string {
  return cost ? formatCurrency(cost) : '$0';
}

const summaryFields = computed(() => [
  {
    label: 'Pago Semana (líquido)',
    value: formatCostValue(caregiver.value?.defaultWeekCost),
    icon: 'mdi-cash',
  },
  {
    label: 'Pago Fin de Semana (líquido)',
    value: formatCostValue(caregiver.value?.defaultWeekendCost),
    icon: 'mdi-cash-clock',
  },
  {
    label: 'Con boleta de honorarios',
    value: caregiver.value?.defaultWithTaxBill ? 'Sí' : 'No',
    icon: 'mdi-receipt-text',
  },
]);

const validationSchema = object({
  defaultWeekCost: number()
    .required('Campo requerido')
    .typeError('Debe ser un número positivo')
    .min(0, 'Debe ser un número positivo'),
  defaultWeekendCost: number()
    .required('Campo requerido')
    .typeError('Debe ser un número positivo')
    .min(0, 'Debe ser un número positivo'),
  defaultWithTaxBill: boolean().required('Campo requerido'),
});

const { handleSubmit, values, errors, defineField, setValues } = useForm({
  validationSchema,
});

const [defaultWeekCost, defaultWeekCostAttrs] = defineField('defaultWeekCost');
const [defaultWeekendCost, defaultWeekendCostAttrs] = defineField('defaultWeekendCost');
const [defaultWithTaxBill, defaultWithTaxBillAttrs] = defineField('defaultWithTaxBill');

const {
  mutate: updateDefaultCosts,
  isPending: isUpdatePending,
  isError: isUpdateError,
} = useMutation<unknown, AxiosError>({
  mutationFn: () => caregiversApi.updateDefaultCosts(
    props.caregiverId,
    values as {
      defaultWeekCost: number;
      defaultWeekendCost: number;
      defaultWithTaxBill: boolean;
    },
  ),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['caregiver', props.caregiverId] });
    queryClient.invalidateQueries({ queryKey: ['caregivers'] });
    showForm.value = false;
    showSuccessAlert.value = true;
  },
});

const onSubmit = handleSubmit(() => {
  updateDefaultCosts();
});

const DEFAULT_PRICING = { defaultWeekCost: 0, defaultWeekendCost: 0, defaultWithTaxBill: false };

function getDefaultValuesFromBasePrice() {
  const base = baseServicePrice.value;
  if (!base) return DEFAULT_PRICING;

  return {
    defaultWeekCost: base.weekCost ?? 0,
    defaultWeekendCost: base.weekendCost ?? 0,
    defaultWithTaxBill: base.withTaxBill ?? false,
  };
}

function openForm() {
  if (hasExistingCosts.value && caregiver.value) {
    setValues({
      defaultWeekCost: caregiver.value.defaultWeekCost ?? 0,
      defaultWeekendCost: caregiver.value.defaultWeekendCost ?? 0,
      defaultWithTaxBill: caregiver.value.defaultWithTaxBill ?? false,
    });
  } else {
    setValues(getDefaultValuesFromBasePrice());
  }
  showSuccessAlert.value = false;
  showForm.value = true;
}

function closeForm() {
  showForm.value = false;
}

const isFirstTimeSetup = computed(() =>
  !hasExistingCosts.value && showForm.value,
);

const SUCCESS_ALERT_DURATION_MS = 5000;

watch(showSuccessAlert, (val) => {
  if (!val) return;

  setTimeout(() => {
    showSuccessAlert.value = false;
  }, SUCCESS_ALERT_DURATION_MS);
});
</script>

<template>
  <div v-if="hasPermission('service_prices', 'index')">
    <v-card :loading="isCaregiverLoading">
      <v-card-text class="pa-5">
        <section-header
          icon="mdi-currency-usd"
          title="Costos de Servicio"
          class="mb-5"
        >
          <v-btn
            v-if="hasPermission('service_prices', 'update')"
            color="primary"
            variant="outlined"
            size="small"
            class="ml-3"
            @click="openForm"
          >
            <v-icon
              icon="mdi-pencil"
              class="mr-1"
            />
            {{ hasExistingCosts ? 'Editar' : 'Configurar' }}
          </v-btn>
        </section-header>

        <v-alert
          v-if="showSuccessAlert"
          type="success"
          variant="tonal"
          class="mb-4"
        >
          Los costos de servicio han sido actualizados correctamente.
        </v-alert>

        <v-row v-if="hasExistingCosts && caregiver">
          <v-col
            v-for="field in summaryFields"
            :key="field.label"
            cols="12"
            sm="6"
            md="4"
          >
            <info-field
              :icon="field.icon"
              :label="field.label"
              :value="field.value"
            />
          </v-col>
        </v-row>

        <v-alert
          v-else-if="!isCaregiverLoading"
          type="info"
          variant="tonal"
        >
          Este profesional no tiene costos de servicio configurados.
        </v-alert>
      </v-card-text>

      <v-card-actions v-if="!props.inline">
        <v-btn
          variant="text"
          @click="emit('close')"
        >
          Cerrar
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-dialog
      v-model="showForm"
      max-width="600"
    >
      <v-card :loading="isCaregiverLoading || isBaseServicePriceLoading">
        <v-card-title>
          <span class="text-h5">
            {{ hasExistingCosts ? 'Editar' : 'Configurar' }} Costos de Servicio
          </span>
        </v-card-title>
        <v-card-text>
          <v-alert
            v-if="isFirstTimeSetup"
            type="warning"
            class="mb-4"
          >
            Se completaron los campos utilizando los valores base asignados
            al profesional según la configuración de la empresa.
          </v-alert>
          <v-alert
            v-if="defaultWeekCost === 0 || defaultWeekendCost === 0"
            type="warning"
            class="mb-4"
          >
            En caso de campos vacíos, el valor quedará como 0.
          </v-alert>
          <v-row>
            <v-col
              cols="12"
              md="6"
            >
              <price-input
                v-model="defaultWeekCost"
                v-bind="defaultWeekCostAttrs"
                label="Pago Semana (líquido)"
                :error-messages="errors.defaultWeekCost"
              />
            </v-col>
            <v-col
              cols="12"
              md="6"
            >
              <price-input
                v-model="defaultWeekendCost"
                v-bind="defaultWeekendCostAttrs"
                label="Pago Fin de Semana (líquido)"
                :error-messages="errors.defaultWeekendCost"
              />
            </v-col>
            <v-col cols="12">
              <v-radio-group
                v-model="defaultWithTaxBill"
                v-bind="defaultWithTaxBillAttrs"
                label="Con boleta de honorarios"
                inline
              >
                <v-radio
                  label="Sí"
                  :value="true"
                />
                <v-radio
                  label="No"
                  :value="false"
                />
              </v-radio-group>
            </v-col>
          </v-row>
          <v-alert
            v-if="isUpdateError"
            type="error"
            class="mt-2"
          >
            Error al actualizar los costos de servicio.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-btn
            variant="text"
            @click="closeForm"
          >
            Cancelar
          </v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            variant="text"
            :loading="isUpdatePending"
            @click="onSubmit"
          >
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

